import { Baseline, OneOfElements, Project } from '../models/project.model';
import { findDuplicates } from '../helpers/array.helpers';
import {
  getElementName,
  isElementWithGeneratedChildren,
  isGenerated,
  isMaintenanceElement,
} from '../helpers/element.helpers';
import { getElementAndQuantityProperties } from '../helpers/element-property.helpers';
import { hasTruthyProperties } from '../helpers/object.helpers';
import { getMissingProductIdsInVersion } from '../helpers/product.helpers';
import { getProjectMeta } from '../helpers/project.helpers';
import {
  ElementPath,
  flattenElements,
  getAllBuildingVersions,
  getChildElements,
  isBuildingVersionElement,
  isElement,
  isProductElement,
} from '../helpers/recursive-element.helpers';
import { isProjectInfoOrFolder } from '../helpers/project-folder.helpers';
import { getCrossVersionDuplicateProposalIdsFromVersions } from '../helpers/proposal-deduplication.helpers';
import {
  isValidProposals,
  isValidProposalsAndActions,
} from './proposals-validation';
import { throwValidationErrors } from './validation.helpers';
import { cacheFactory, required } from '../helpers/function.helpers';
import {
  isValidElementProperties,
  isValidElementQuantityProperties,
} from './element-property.validation';
import { ArrayOrSingle } from '../models/type-helpers.model';
import { getPathInFlatTree } from '../helpers/tree.helpers';
import {
  isProductCategory,
  isProductCategoryElement,
  isServiceCategory,
  isSystemCategory,
} from '../helpers/element-category.helpers';
import { logOutsideTest } from '@shared/helpers/debug.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { isValidProduct } from './product-validation';
import { isValidProductElement } from './product-element-validation.helpers';
import {
  ElementValidationError,
  GenericValidationError,
  SELECT_COUNT_NOT_IN_OPTIONS_MESSAGE,
  ValidationResult,
} from './validation.models';
import { isValidOwnerAndOrganizations } from './organization.validation';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import { isValidBuildingDesign } from '../helpers/design/validation/design.validation';
import { isValidProjectMetadata } from './project-metadata.validation';

export const ProjectValidationErrors = {
  UNHANDLED_ERROR: 'Critical unhandled error',
  ANCESTOR_HAS_MAINTENANCE_CATEGORY:
    'Maintenance elements cannot have other maintenance elements as children',
  CANNOT_MIX_GENERATED_AND_NOT_GENERATED_ELEMENTS:
    "Can't mix generated and not generated elements",
  DUPLICATE_IDS: 'Duplicate ids in project',
  ELEMENT_MISSING_ID_OR_ELEMENTS: 'Element is missing id or elements property',
  INVALID_COUNT: 'Invalid count',
  INVALID_UNIT: 'Invalid unit',
  INVALID_ELEMENT: 'Invalid element',
  INVALID_ELEMENT_KIND: 'Invalid kind for element',
  INVALID_ELEMENT_VERSION: 'Invalid element version',
  INVALID_ELEMENT_VERSIONS: 'Invalid element versions in project',
  INVALID_PROPERTY_NAME: 'Invalid property name',
  INVALID_PROPERTY_SOURCE: 'Invalid property source',
  INVALID_METADATA: 'Invalid metadata',

  PRODUCT_ELEMENT_MISSING_ID_OR_PRODUCT_ID:
    'Product element is missing id or product_id',
  PROJECT_HAS_NO_VERSIONS: 'Project has no versions',
  PROJECT_NOT_DEFINED: 'Project is not defined',
  VERSION_HAS_PRODUCT_ELEMENTS_CHILDREN:
    'Version has product elements as direct children',
  VERSION_NOT_DEFINED: 'Version is not defined',
  VERSION_HAS_NO_RESULTS: 'Version has no results',
  /**
   * Legacy `baselines[].recipes` blob (formerly `buildings[].versions[].recipes`).
   * Forbidden once the DEV-3491 recipe-library DB migration has materialized
   * recipe children onto hosts and dropped the embedded catalog. Unmigrated
   * databases still carrying that key will fail validation until the migration
   * has run (DEV-3701).
   */
  VERSION_HAS_EMBEDDED_RECIPES:
    'Baseline must not define recipes (legacy embedded catalog removed by DEV-3491 migration)',
  PRODUCT_CATEGORY_ELEMENT_WITH_NON_GENERATED_CHILDREN:
    'Product category element with non-generated children',

  // TODO Use this structure for all validation errors
  PROPERTY: {
    SELECT: {
      INVALID_COUNT: 'Invalid count for type select',
      NO_COUNT_OR_FALLBACK_COUNT:
        'Property of type select has no count or fallbackCount',
      COUNT_NOT_IN_OPTIONS: SELECT_COUNT_NOT_IN_OPTIONS_MESSAGE,
      NO_OPTIONS: 'Property of type select has no options',
      INVALID_OPTIONS: 'Options must either be an array or a string key',
    },
  },
  PROPOSAL: {
    VERSION_HAS_NO_PROPOSALS: 'Version must have at least one proposal',
    MISSING_PROPERTIES: 'Proposal is missing properties',
    MISSING_NAME: 'Proposal must have a name',
    MISSING_RESULTS_RECORD_IDS: 'Proposal is missing ids in resultsRecord',
    ORPHAN_ACTIONS:
      'Version has actions that are not referenced by any proposals',
    UNKNOWN_ACTION_IDS:
      'proposals references action id(s) missing from version.actions',
    EMPTY_ACTIONS: 'Version has actions with no changes',
    FORBIDDEN_CHANGES_IN_PROPOSAL_ACTIONS:
      'Proposal actions must not contain baseline-only project changes (see isProjectChangeExcludedFromProposal)',
    STALE_ELEMENT_REFERENCE:
      'Proposal action references an element id that does not exist in the version tree',
    STALE_PROPERTY_REFERENCE:
      'Proposal action references a property id that does not exist on the target element',
    STALE_PROPERTY_NAME_REFERENCE:
      'Proposal action references a property name that does not exist on the target element (nor added by an earlier ElementCategoryEnrich / ElementPropertyAdd in the same proposal)',
    STALE_QUANTITY_NAME_REFERENCE:
      'Proposal action references a quantity name that does not exist on the target element',
    STALE_CHILD_REFERENCE:
      'Proposal action references a child element id (removeChildIds) that does not exist on the target element',
    DUPLICATE_SLOT_IN_PROPOSAL:
      'A proposal has multiple actions containing changes that target the same element + slot (same property/quantity/visibility)',
    DUPLICATE_CHANGE_TYPE_IN_ACTION_SLOT:
      'An action contains multiple changes of the same type targeting the same element + slot',
    DUPLICATE_PROPOSAL_ID_ACROSS_VERSIONS:
      'Same proposal id appears on more than one building version (DEV-3313)',
  },
  CONVERSION_FACTORS: {
    MISSING_NON_CO2E_VALUE_OF_1:
      'Conversion factors must have at least one non-CO2e value of 1',
    MISSING_CO2E_UNIT: 'Conversion factors must include at least one CO2e unit',
    INVALID_VALUE: 'Value must be a non zero number',
  },
} as const;

/**
 * Test if project is valid or not
 * @param project
 * @param verbose
 * @returns
 */
export const isValidProject = (
  project: Project | undefined,
): ValidationResult => {
  if (!project) {
    return new GenericValidationError(
      ProjectValidationErrors.PROJECT_NOT_DEFINED,
    );
  }

  const design = getBuildingDesignFromProjectOrBuilding(project);
  const validDesign = isValidBuildingDesign(design);
  if (validDesign !== true) {
    return validDesign;
  }

  const validMeta = isValidProjectMetadata(getProjectMeta(project));

  if (validMeta !== true) {
    return validMeta;
  }

  // Ids must be unique within project
  const validUniqueIds = isElementAndPropertyIdsUnique(
    flattenElements(project),
  );
  if (validUniqueIds !== true) {
    return validUniqueIds;
  }

  const buildingVersions = getAllBuildingVersions(project);

  if (!project.active_version_id) {
    return new GenericValidationError('Project has no active version id');
  }
  // Check that the active version is in the project
  else {
    const activeVersion = buildingVersions.find(
      (v) => v.id === project.active_version_id,
    );
    if (!activeVersion) {
      return new GenericValidationError('Active version not found in project');
    }
  }

  // Check that the project has a valid owner and organization
  const validOwnerAndOrganizations = isValidOwnerAndOrganizations(project);
  if (validOwnerAndOrganizations !== true) {
    return validOwnerAndOrganizations;
  }

  // Cross-version proposal id collisions break {@link findProposalLocation} routing (DEV-3313).
  const validProposalIds = isProposalIdsUniqueAcrossVersions(buildingVersions);
  if (validProposalIds !== true) {
    return validProposalIds;
  }

  // Check that all versions are valid
  return isValidBuildingVersions(buildingVersions);
};

/**
 * Same proposal id may exist at most once across every {@link Baseline} of a project.
 * Duplicates make {@link findProposalLocation} return the first match (deterministic but
 * surprising) and silently route saves to the wrong version (DEV-3313).
 */
const isProposalIdsUniqueAcrossVersions = (
  versions: Baseline[],
): ValidationResult => {
  const duplicates = getCrossVersionDuplicateProposalIdsFromVersions(versions);
  if (duplicates.length === 0) return true;
  return new GenericValidationError(
    `${ProjectValidationErrors.PROPOSAL.DUPLICATE_PROPOSAL_ID_ACROSS_VERSIONS}: ${duplicates.join(', ')}`,
  );
};

/**
 * Check that all versions are valid and that at least one version is present
 * @param versions
 * @returns
 */
const isValidBuildingVersions = (
  versions: Baseline[] | undefined,
): ValidationResult => {
  if (!versions?.length) {
    return new GenericValidationError(
      ProjectValidationErrors.PROJECT_HAS_NO_VERSIONS,
    );
  }
  for (const version of versions) {
    const isValid = isValidVersion(version);
    if (isValid !== true) {
      return isValid;
    }
  }
  return true;
};

/**
 * Check if all element and property ids are unique and not duplicated in other elements
 * @param elements
 * @returns
 */
export const isElementAndPropertyIdsUnique = (
  singleElementOrArray: ArrayOrSingle<OneOfElements>,
  verbose = false,
): ValidationResult => {
  const elements = Array.isArray(singleElementOrArray)
    ? singleElementOrArray
    : flattenElements(singleElementOrArray);

  const ids = elements.flatMap((e) => [
    e.id,
    ...getElementAndQuantityProperties(e).map((p) => p.id),
  ]);
  const duplicates = findDuplicates(ids);
  if (duplicates.length) {
    if (verbose) {
      const names = elements
        .filter((e) => duplicates.includes(e.id))
        .map((e) => getElementName(e, undefined, 'ProductElement: ' + e.id));
      console.error(
        'Duplicate element ids in project. Elements: ',
        names,
        duplicates,
      );
    }
    return new GenericValidationError(ProjectValidationErrors.DUPLICATE_IDS);
  }
  return true;
};

/**
 * Throw an error if project is not valid.
 * Returns the project if it is valid so that it can be used in a chain.
 * @param project
 */
export const validateProject = (
  project: Project | undefined,
  onError?: (error: string) => void,
): Project => {
  throwValidationErrors(
    isValidProject(project),
    'ProjectValidationError',
    onError,
  );
  return required(project);
};

/**
 * Check that all elements in the project are valid. Will not check recursively.
 * @param elements
 * @returns
 */
export const isValidElements = (
  elements: OneOfElements[],
): ValidationResult => {
  for (const element of elements) {
    // Avoid infinite loop (but need building version for the path)
    if (isBuildingVersionElement(element)) {
      continue;
    }
    const path = getPathInFlatTree(elements, element, 'elements');
    const isValid = isValidElement(element, path as ElementPath);

    if (isValid !== true) {
      return isValid;
    }
  }
  return true;
};

/**
 * Test if version is valid or not.
 *
 * Rejects a legacy `recipes` key on the {@link Baseline} JSON
 * ({@link ProjectValidationErrors.VERSION_HAS_EMBEDDED_RECIPES}). That invariant
 * applies only after the DEV-3491 recipe-library DB migration; run the migration
 * before relying on this check against real project data.
 *
 * @param version
 * @param verbose
 * @returns
 */
export const isValidVersion = (
  version: Baseline | undefined,
): ValidationResult => {
  if (!version) {
    return new ElementValidationError(
      ProjectValidationErrors.VERSION_NOT_DEFINED,
      version,
    );
  }

  // Only revalidate when version changes (expensive operation if multiple versions exist)
  return cacheFactory(
    () => {
      // Post DEV-3491 migration invariant: recipe catalog lives in `recipes` table / store,
      // never on the baseline JSON. Own-key presence means unmigrated or illegally reintroduced.
      if (Object.prototype.hasOwnProperty.call(version, 'recipes')) {
        return new ElementValidationError(
          ProjectValidationErrors.VERSION_HAS_EMBEDDED_RECIPES,
          { element: version },
        );
      }

      const rootElements = getChildElements(version);

      // Check that we don't have any product elements as direct children to the version
      if (rootElements.some(isProductElement)) {
        return new ElementValidationError(
          ProjectValidationErrors.VERSION_HAS_PRODUCT_ELEMENTS_CHILDREN,
          { element: version },
        );
      }

      // Check that all elements in the version are valid
      const validChildren = isValidElements(flattenElements(version));

      // String if error, true if valid
      if (validChildren !== true) {
        return validChildren;
      }

      const validProposals = isValidProposals(version);

      // String if error, true if valid
      if (validProposals !== true) {
        return validProposals;
      }

      const validProposalsActions = isValidProposalsAndActions(version);
      if (validProposalsActions !== true) {
        return validProposalsActions;
      }

      // Check that we map all productElements to a product
      const missing = getMissingProductIdsInVersion(version);

      const versionResults = version.resultsRecord?.[version.id];

      if (versionResults === undefined) {
        return new ElementValidationError(
          ProjectValidationErrors.VERSION_HAS_NO_RESULTS,
          { element: version },
        );
      }
      if (missing.length > 0) {
        return new ElementValidationError(
          `Missing product ids in version ${version.name}`,
          { element: version },
        );
      }
      // TODO: This should not happen, but it does
      // const obsolete = getObsoleteProductIdsInVersion(version);
      // if (obsolete.length > 0) {
      // Don't return an error, just log a warning since this is not critical
      // console.warn(`Obsolete product ids in version ${version.name}`);
      // for (const productId of obsolete) {
      //   const product = products[productId];
      //   if (product) {
      //     console.info(`Obsolete product: ${product.name} (${productId})`);
      //   }
      // }
      // }

      for (const product of Object.values(version.products)) {
        const validProduct = isValidProduct(product);
        if (validProduct !== true) {
          return validProduct;
        }
      }

      return true;
    },
    `isValidVersion[${version.id}]`,
    [version],
  );
};

export const validateElement = <T extends OneOfElements>(
  element: OneOfElements,
): T => {
  throwValidationErrors(isValidElement(element));
  return element as T;
};

/**
 * Check that an element is valid
 * @param element
 * @param path
 * @param isRecipe - Whether the element is part of a recipe (recipes don't have element ids)
 * @returns true if valid, error message if invalid
 */
export const isValidElement = (
  element: OneOfElements,
  path: ElementPath = [],
): ValidationResult => {
  if (isProjectInfoOrFolder(element)) {
    return new GenericValidationError(
      'No folders or projects allowed in project validation',
    );
  }

  if (isBuildingVersionElement(element)) {
    return isValidVersion(element);
  }

  // Validate productElement
  if (isProductElement(element)) {
    return isValidProductElement(element, path);
  }
  // Validate element
  else if (isElement(element)) {
    const { category_id } = element;

    // Unset / None is allowed (no category selected). Warn only for a non-empty unknown id.
    if (
      guards.is.nonEmptyString(category_id) &&
      !isSystemCategory(category_id) &&
      !isProductCategory(category_id) &&
      !isServiceCategory(category_id)
    ) {
      console.warn(`Invalid category_id ${category_id} on element`, {
        element,
      });
    }

    if (!hasTruthyProperties(element, 'elements')) {
      return new ElementValidationError(
        ProjectValidationErrors.ELEMENT_MISSING_ID_OR_ELEMENTS,
        { element },
      );
    }
    const validProperties = isValidElementProperties(element);
    if (validProperties !== true) {
      return validProperties;
    }
    const validQuantities = isValidElementQuantityProperties(element);
    if (validQuantities !== true) {
      return validQuantities;
    }

    const children = getChildElements(element);

    // Elements whose category drives child structure (e.g. Concrete) must only contain
    // product children produced by the template. Other elements have no constraint here.
    if (
      isElementWithGeneratedChildren(element) &&
      !children.every(isGenerated)
    ) {
      return new ElementValidationError(
        ProjectValidationErrors.CANNOT_MIX_GENERATED_AND_NOT_GENERATED_ELEMENTS,
        { element },
      );
    }

    // Product categories must only have generated children
    if (isProductCategoryElement(element)) {
      if (!children.every(isGenerated)) {
        return new ElementValidationError(
          ProjectValidationErrors.PRODUCT_CATEGORY_ELEMENT_WITH_NON_GENERATED_CHILDREN,
          { element },
        );
      }
      // TODO: Should be an error?
      if (children.length === 0) {
        logOutsideTest.warn(
          `Product category element ${element.id} has no children`,
        );
      }
    }

    if (isMaintenanceElement(element) && path.some(isMaintenanceElement)) {
      return new ElementValidationError(
        ProjectValidationErrors.ANCESTOR_HAS_MAINTENANCE_CATEGORY,
        { element },
      );
    }
  }
  // Invalid element type, should never happen
  else {
    return new ElementValidationError(
      ProjectValidationErrors.INVALID_ELEMENT_KIND,
      element,
    );
  }
  return true;
};
